/**
 * Users and their devices. Owner-only surface — every route here is gated on `user.view`,
 * `user.create`, `user.edit`, `user.deactivate` or `user.revoke_devices`, checked server-side on
 * every call the same as everywhere else.
 *
 * Revoking a device is a narrow write: it forces every active device off, nothing else about the
 * user changes, so it invalidates only the device list and the summary row's `activeDeviceCount`.
 * Create, edit, deactivate and reactivate all touch fields the list itself renders (name, role,
 * shop, active state), so they invalidate `UserList` as well as the one row — a user list that
 * doesn't move when someone is deactivated is worse than a slightly wider refetch.
 */

import { api } from '../api.js';
import { userListTag, userTag } from '../tags.js';
import type { Paged, RoleId, ShopId, UserDevice, UserId, UserSummary } from '../../types/api.js';

export type ListUsersParams = {
  readonly search?: string | undefined;
  readonly shopId?: string | undefined;
  readonly includeInactive?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type CreateUserBody = {
  readonly username: string;
  readonly fullName: string;
  readonly password: string;
  readonly roleId: RoleId;
  readonly homeShopId: ShopId;
  readonly phone?: string | undefined;
  readonly email?: string | undefined;
};

export type UpdateUserBody = {
  readonly fullName: string;
  readonly roleId: RoleId;
  readonly homeShopId: ShopId;
  readonly phone?: string | undefined;
  readonly email?: string | undefined;
};

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    listUsers: build.query<Paged<UserSummary>, ListUsersParams>({
      query: (params) => ({ url: '/users', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result) => [
        userListTag(),
        ...(result?.items ?? []).map((user) => userTag(user.id)),
      ],
    }),

    getUser: build.query<UserSummary, UserId>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [userTag(id)],
    }),

    listUserDevices: build.query<UserDevice[], UserId>({
      query: (id) => `/users/${id}/devices`,
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}:devices` }],
    }),

    /** Signs the user out everywhere. They must use their password next. */
    revokeUserDevices: build.mutation<number, UserId>({
      query: (id) => ({ url: `/users/${id}/revoke-devices`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        userTag(id),
        { type: 'User', id: `${id}:devices` },
      ],
    }),

    createUser: build.mutation<UserId, CreateUserBody>({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      invalidatesTags: [userListTag()],
    }),

    updateUser: build.mutation<void, { id: UserId; body: UpdateUserBody }>({
      query: ({ id, body }) => ({ url: `/users/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [userTag(id), userListTag()],
    }),

    /** `reason` is required server-side — the owner must name why, not just click a button. */
    deactivateUser: build.mutation<void, { id: UserId; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/users/${id}/deactivate`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { id }) => [userTag(id), userListTag()],
    }),

    reactivateUser: build.mutation<void, UserId>({
      query: (id) => ({ url: `/users/${id}/reactivate`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [userTag(id), userListTag()],
    }),

    /** Also clears the user's PIN and revokes every device, server-side — the dialog says so. */
    resetUserPassword: build.mutation<void, { id: UserId; newPassword: string }>({
      query: ({ id, newPassword }) => ({
        url: `/users/${id}/reset-password`,
        method: 'POST',
        body: { newPassword },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        userTag(id),
        { type: 'User', id: `${id}:devices` },
      ],
    }),
  }),
});

export const {
  useListUsersQuery,
  useGetUserQuery,
  useListUserDevicesQuery,
  useRevokeUserDevicesMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useResetUserPasswordMutation,
} = usersApi;
